import { handleResponseOld, handleResponseUseItemApiError, handleResponseWithNon200Errors, makePost } from './helpers';

/**
 * @category User API
 * @see postChangePassword
 */
type PostChangePasswordParams = {
    authToken: string;
    deployment: string;
    newPassword: string;
    oldPassword: string;
};

/**
 * postChangePassword - updates user password
 * @category User API
 * @function postChangePassword
 * @param {PostChangePasswordParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/updatepassword
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/p4t7pq/methods/POST
 */
export const postChangePassword = ({ authToken, deployment, newPassword, oldPassword }: PostChangePasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'updatepassword', authToken, deployment, path: '<USER-API>' });
        request.send({ newPassword, oldPassword });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postConfirmPassword
 */
type PostConfirmPasswordParams = {
    deployment: string;
    password: string;
    username: string;
};

/**
 * postConfirmPassword - login process, confirms password and returns auth token for login
 * @category Item API
 * @function postConfirmPassword
 * @param {PostConfirmPasswordParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/spalogin
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-login.ts
 */
export const postConfirmPassword = ({ deployment, password, username }: PostConfirmPasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spalogin' });
        request.send({ password, username });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postForgotPassword
 */
type PostForgotPasswordParams = {
    deployment: string;
    emailAddress: string;
    sendSMS: boolean;
};

/**
 * postForgotPassword - sends request for password recovery notification
 * @category User API
 * @function postForgotPassword
 * @param {PostForgotPasswordParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/forgotpassword
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/ywmkti/methods/POST
 */
export const postForgotPassword = ({ deployment, emailAddress, sendSMS }: PostForgotPasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'forgotpassword',
            deployment,
            path: '<USER-API>',
        });
        request.send({ sendSMS, username: emailAddress });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postResetPassword
 */
type PostResetPasswordParams = {
    bidderId: number;
    deployment: string;
    hash: string;
    newPassword: string;
};

/**
 * postResetPassword - sends password reset information
 * @category Item API
 * @function postResetPassword
 * @param {PostResetPasswordParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/reset-password
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-reset-password.ts
 */
export const postResetPassword = ({ bidderId, deployment, hash, newPassword }: PostResetPasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spa/reset-password' });
        request.send({ bidderId, hash, newPassword });
        request.end((err, response) => handleResponseUseItemApiError({ err, reject, resolve, response }));
    });
